import { Box, Button, TextField, Typography } from "@outschool/backpack";
import { useLocale, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import _ from "lodash";
import React from "react";

import { yearOptionsExpanded } from "../../../shared/Learner";
import { MAX_LEARNER_AGE, MIN_LEARNER_AGE } from "../../../shared/User";
import { LearnerFormWarning } from "../learner/LearnerFormWarning";
import { LearnerNameWarning } from "../learner/LearnerNameWarning";
import Selector from "../onboarding/components/Selector";
import { SquareButton } from "../onboarding/components/SquareButton";
import { SCHOOLING_APPROACH_SELECTIONS } from "../onboarding/consts";
import { getAgeFromBirthMonthAndYear } from "../onboarding/utils";

interface RequiredLearnerDataCollectionFormProps {
  onSubmit: (data: RequiredLearnerData) => void;
}

export interface RequiredLearnerData {
  name: string;
  birthMonth?: number;
  birthYear?: number;
  schoolingApproach: string;
}

const RequiredLearnerDataCollectionForm: React.FC<
  RequiredLearnerDataCollectionFormProps
> = ({ onSubmit }) => {
  const locale = useLocale();
  const { t } = useTranslation("client\\components\\onboarding\\LearnerIdStep");

  const [data, setData] = React.useState<RequiredLearnerData>({
    name: "",
    schoolingApproach: ""
  });

  const isAgeInvalid = React.useMemo(() => {
    if (!data.birthMonth || !data.birthYear) {
      return false;
    }
    const age = getAgeFromBirthMonthAndYear(data.birthMonth, data.birthYear);
    return age < MIN_LEARNER_AGE || age > MAX_LEARNER_AGE;
  }, [data.birthMonth, data.birthYear]);

  const isValid =
    data.name.trim() &&
    data.schoolingApproach &&
    data.birthMonth &&
    data.birthYear &&
    !isAgeInvalid;

  return (
    <Box sx={{ paddingRight: "2em" }}>
      <Typography variant="h3" align="center" sx={{ marginBottom: 24 }}>
        {t("Who’s enrolling?")}
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "primary.50",
          borderRadius: 8,
          padding: 20,
          gap: 20,
          boxShadow: "0px 1px 8px 0px #0000000A, 0px 1px 4px 0px #0000000A"
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: "0.75rem"
          }}
        >
          <Typography data-private variant="h6" sx={{ marginLeft: "0.75rem" }}>
            {t("First name")}
          </Typography>
          <TextField
            autoComplete="off"
            value={data.name}
            onChange={e => {
              setData({
                ...data,
                name: e.target.value
              });
            }}
          />
          <LearnerNameWarning name={data.name} />
        </Box>

        <Box
          flex
          sx={{
            flexDirection: "column",
            marginTop: "1.5rem",
            gap: "0.75rem"
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: "0.75rem" }}>
            {t("Birthday")}
          </Typography>
          <Box flex sx={{ width: "100%", gap: "1rem" }}>
            <Selector
              options={_.range(1, 13).map(monthIdx => ({
                value: monthIdx,
                label: dayjs()
                  .locale(locale)
                  .month(monthIdx - 1)
                  .format("MMMM")
              }))}
              value={data.birthMonth || null}
              onChange={month => {
                setData({
                  ...data,
                  birthMonth: month
                });
              }}
              emptyLabel={t("Month")}
              t={t}
            />
            <Selector
              options={yearOptionsExpanded.map(opt => ({
                value: opt,
                label: opt
              }))}
              value={data.birthYear || null}
              onChange={year => {
                setData({
                  ...data,
                  birthYear: year
                });
              }}
              emptyLabel={t("Year")}
              t={t}
            />
          </Box>
        </Box>
        {isAgeInvalid && (
          <LearnerFormWarning
            message={t(
              "Your learner must be between 1 and 18 years old to join."
            )}
          />
        )}

        <Box
          flex
          sx={{
            flexDirection: "column",
            marginTop: "4.5rem",
            gap: "1.25rem"
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: "0.75rem" }}>
            {t("Where does your kid learn most days?")}
          </Typography>
          <Box
            flex
            sx={{
              flexDirection: "column",
              gap: "0.75rem"
            }}
          >
            {SCHOOLING_APPROACH_SELECTIONS.map(
              ({ value, label, icon, subtitle }, index) => (
                <SquareButton
                  key={index}
                  icon={icon}
                  onClick={() => {
                    setData({
                      ...data,
                      schoolingApproach: value
                    });
                  }}
                  isSelected={data.schoolingApproach === value}
                >
                  <Box flex sx={{ flexDirection: "column", width: "100%" }}>
                    <Typography variant="h6" align="left">
                      {label}
                    </Typography>
                    <Typography variant="body1" align="left">
                      {subtitle}
                    </Typography>
                  </Box>
                </SquareButton>
              )
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: "2.4rem"
        }}
      >
        <Button
          variant="contained"
          onClick={() => onSubmit(data)}
          disabled={!isValid}
        >
          {t("Save and continue")}
        </Button>
      </Box>
    </Box>
  );
};

export default RequiredLearnerDataCollectionForm;
